import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import ContentProtectionWrapper from "@/components/ContentProtectionWrapper";
import { ThemeProvider } from "@/components/ThemeProvider";

// Eager load the home page for best LCP
import Index from "./pages/Index";

// Lazy load other pages
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Solutions = lazy(() => import("./pages/Solutions"));
const Services = lazy(() => import("./pages/Services"));
const Clients = lazy(() => import("./pages/Clients"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));

// SEO landing pages (not in navigation — direct URL + sitemap only)
const ZLDPlantManufacturerIndia = lazy(() => import("./pages/seo/ZLDPlantManufacturerIndia"));
const IndustrialEvaporatorManufacturer = lazy(() => import("./pages/seo/IndustrialEvaporatorManufacturer"));
const MultipleEffectEvaporatorManufacturer = lazy(() => import("./pages/seo/MultipleEffectEvaporatorManufacturer"));

// Dev-only visual harness (excluded from production builds)
const AIQuoteCardPreview = import.meta.env.DEV
  ? lazy(() => import("./pages/dev/AIQuoteCardPreview"))
  : null;

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <ContentProtectionWrapper>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:slug" element={<ProductDetail />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/services" element={<Services />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                <Route path="/thank-you" element={<ThankYou />} />
                {/* SEO landing pages — direct URL access only, not in nav */}
                <Route path="/zld-plant-manufacturer-india" element={<ZLDPlantManufacturerIndia />} />
                <Route path="/industrial-evaporator-manufacturer" element={<IndustrialEvaporatorManufacturer />} />
                <Route path="/multiple-effect-evaporator-manufacturer" element={<MultipleEffectEvaporatorManufacturer />} />
                {AIQuoteCardPreview && (
                  <Route path="/dev/ai-quote-card" element={<AIQuoteCardPreview />} />
                )}
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ContentProtectionWrapper>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
